import { usePostOrganizationApi, usePutOrganizationApi } from 'api/organization'
import { Flex } from 'components/shared/flex'
import { InputField, SelectField } from 'components/shared/input'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useContext, useEffect, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { Form, useEffectSkipFirst, useForm } from 'utils/hooks'
import { ApiError } from 'utils/network/api_hooks'
import { Organization, OrganizationForm } from 'entities/organization'
import { AddressFormView } from 'components/address/address_form'
import { useFetchShipmentTaskItemTemplatesApi } from 'api/shipment_task_item_template'
import { ShipmentTaskItemTemplate } from 'entities/shipment_task_item_template'

const FormView = (form: Form<OrganizationForm>, apiError: ApiError, shipmentTaskItemTemplates: ShipmentTaskItemTemplate[]) => {
    return (
        <>
            <Flex>
                <InputField form={form} attr="name" apiError={apiError} label="施設名" />
            </Flex>
            <InputField onlyHankaku form={form} attr="tel" apiError={apiError} label="電話番号(ハイフンあり)" placeholder="xx-xxxx-xxxx" />
            <AddressFormView form={form} apiError={apiError} />
            <SelectField
                form={form}
                attr="newShipmentTaskItemTemplateId"
                apiError={apiError}
                label="初回テンプレート"
                includeBlank
                selectItems={shipmentTaskItemTemplates.map((template) => ({
                    label: template.name,
                    value: template.id,
                }))}
            />
            <SelectField
                form={form}
                attr="addShipmentTaskItemTemplateId"
                apiError={apiError}
                label="追加テンプレート"
                includeBlank
                selectItems={shipmentTaskItemTemplates.map((template) => ({
                    label: template.name,
                    value: template.id,
                }))}
            />
        </>
    )
}

type NewOrganizationFormProps = {
    onComplete?: () => void
}

export const NewOrganizationForm = (props: NewOrganizationFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePostOrganizationApi()
    const form = useForm<OrganizationForm>({ address: {} })
    const [show, setShow] = useState<boolean>(false)
    const shipmentTaskItemTemplatesApi = useFetchShipmentTaskItemTemplatesApi()

    useEffect(() => {
        shipmentTaskItemTemplatesApi.execute()
    }, [])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                setShow(false)
                props.onComplete()
            }
        }
    }, [api.loading])

    const handleClose = () => {
        setShow(false)
    }

    return (
        <>
            <Button
                variant="success"
                onClick={() => {
                    setShow(true)
                }}
            >
                新規登録
            </Button>
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>組織の登録</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <>{FormView(form, api.apiError, shipmentTaskItemTemplatesApi.response.results)}</>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        閉じる
                    </Button>
                    <Button variant="success" onClick={handleUpdate}>
                        登録
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

type EditOrganizationFormProps = {
    organization: Organization
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditOrganizationForm = (props: EditOrganizationFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutOrganizationApi()
    const form = useForm<OrganizationForm>({ ...props.organization, institutions: [] })
    const shipmentTaskItemTemplatesApi = useFetchShipmentTaskItemTemplatesApi()

    useEffect(() => {
        shipmentTaskItemTemplatesApi.execute()
    }, [])

    useEffectSkipFirst(() => {
        form.set({ ...props.organization, institutions: [] })
    }, [props.organization])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>組織の編集</Modal.Title>
            </Modal.Header>
            <Modal.Body>{form.object.id && FormView(form, api.apiError, shipmentTaskItemTemplatesApi.response.results)}</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
